
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { mapActions, mapGetters, mapState } from 'vuex';
import { CompanyUserRoleDef } from '@run-diffusion/shared';
import { db, functions } from '@/firebase';
import { TeamUserMixin } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { MemberTeamRef } from '@/utils/membersApi';
import {
	CompanyRoleMembershipDoc,
	CompanyRoleMembershipsByTeamId,
	CompanyUserRoleOutcome,
	CompanyUserRoleTarget,
	EMPTY_COMPANY_USER_ROLE_TARGET,
	PendingInviteClassification,
	PendingInviteReadPlan,
	REMOVE_DIALOG_COPY_VARIANT,
	RemoveDialogCopyVariant,
	buildRemovePayload,
	classifyPendingInvites,
	inviteTeamsLabel,
	isPendingTarget,
	pendingInviteReadPlan,
	removeDialogCopyVariant,
	removeFailureMessage,
	removeOutcome,
} from './companyUserRoleDialogState';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';

/**
 * Remove a live company role, or cancel the role a pending invite would grant.
 * Owned by TeamPage. For a pending target, what cancelling does differs per
 * invitation (one created only to carry the role is withdrawn entirely, an
 * ordinary one is kept without it), so the target's own membership docs are
 * read once on open to say exactly which. They are world-readable, and this is
 * what the `getPendingCompanyRoleInvites` callable used to work out server-side.
 */
export default Vue.extend({
	name: 'RemoveCompanyUserRoleDialog',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		target: {
			type: Object as PropType<CompanyUserRoleTarget>,
			default: () => ({ ...EMPTY_COMPANY_USER_ROLE_TARGET }),
		},
	},
	data () {
		return {
			REMOVE_DIALOG_COPY_VARIANT,
			saving: false,
			// Bumped on every open and close, so reads that land late are dropped.
			openVersion: 0,
			loadingInvites: false,
			loadingRoleDefName: false,
			classification: null as PendingInviteClassification | null,
			classificationIncomplete: false,
			roleDefName: '',
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		companyId (): string | null {
			return this.team?.companyId || null;
		},
		targetEmail (): string {
			return _trim(this.target?.email || '');
		},
		isPending (): boolean {
			return isPendingTarget(this.target);
		},
		loading (): boolean {
			return this.loadingInvites || this.loadingRoleDefName;
		},
		loadingCopy (): string {
			return this.loadingInvites ? 'Checking their pending invitations...' : 'Loading the company role...';
		},
		roleLabel (): string {
			return this.roleDefName || 'company admin';
		},
		copyVariant (): RemoveDialogCopyVariant {
			return removeDialogCopyVariant({
				isPending: this.isPending,
				classification: this.classification,
				classificationIncomplete: this.classificationIncomplete,
				inviteOnlyStub: !!this.target?.row?.inviteOnlyStub,
			});
		},
		withdrawnTeamsLabel (): string {
			return inviteTeamsLabel(this.classification?.withdrawnTeamNames, this.displayLabels.team);
		},
		retainedTeamsLabel (): string {
			return inviteTeamsLabel(this.classification?.retainedTeamNames, this.displayLabels.team);
		},
	},
	watch: {
		value (newVal: boolean) {
			if (newVal) this.init();
			else this.openVersion++;
		},
	},
	beforeDestroy () {
		this.openVersion++;
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		init () {
			const version: number = ++this.openVersion;
			this.saving = false;
			this.roleDefName = '';
			this.classification = null;
			this.classificationIncomplete = false;
			this.loadingInvites = false;
			this.loadingRoleDefName = false;

			this.loadRoleDefName(version);
			if (this.isPending) this.loadPendingInvites(version);
		},
		// One-shot: the name only words the dialog. The dialog waits for it, and the
		// fallbacks cover a read that fails or finds nothing.
		async loadRoleDefName (version: number) {
			const roleDefId: string | null = this.target?.companyUserRoleDefId || null;
			if (!roleDefId) return;

			this.loadingRoleDefName = true;
			let name: string = '';
			try {
				const snap = await db.doc(`companyUserRoleDefs/${roleDefId}`).get();
				const roleDef = snap.data() as CompanyUserRoleDef | undefined;
				name = roleDef?.name || '';
			} catch (error) {
				console.error('Error reading company user role def:', error);
			}
			// A later open owns the flag now; this read belongs to a dialog that closed.
			if (version !== this.openVersion) return;
			this.roleDefName = name;
			this.loadingRoleDefName = false;
		},
		/**
		 * One-shot gets, never binds: the answer words a confirmation and is not
		 * worth a listener per team. Any failed read makes the whole
		 * classification untrustworthy, and the dialog falls back to the generic body.
		 */
		async loadPendingInvites (version: number) {
			const plan: PendingInviteReadPlan = pendingInviteReadPlan(this.target?.row);
			const email: string = this.targetEmail;
			if (!email || !plan.teams.length) {
				this.classificationIncomplete = plan.truncated;
				return;
			}

			this.loadingInvites = true;
			let failed: boolean = false;
			const membershipsByTeamId: CompanyRoleMembershipsByTeamId = {};
			await Promise.all(plan.teams.map(async (team: MemberTeamRef) => {
				try {
					const snap = await db.doc(`teams/${team.id}/teamMembers/${email}`).get();
					membershipsByTeamId[team.id] = snap.exists ? (snap.data() as CompanyRoleMembershipDoc) : null;
				} catch (error) {
					console.error('Error reading pending team membership:', error);
					failed = true;
				}
			}));
			if (version !== this.openVersion) return;

			this.classification = classifyPendingInvites(plan.teams, membershipsByTeamId);
			this.classificationIncomplete = failed || plan.truncated;
			this.loadingInvites = false;
		},
		onResetAndClose () {
			this.$emit('input', false);
		},
		async onConfirm (resetDialog: () => void) {
			if (this.scimProvisioningLocksMembership) {
				if (resetDialog) resetDialog();
				this.onResetAndClose();
				return;
			}
			if (this.saving || this.loading) return;
			// Nothing to send. Close through TeamPage as well as the inner dialog: a reset
			// alone leaves the config open, and no later Remove could open over it.
			if (!this.companyId || !this.targetEmail) {
				if (resetDialog) resetDialog();
				this.onResetAndClose();
				return;
			}

			const roleName: string = this.roleLabel;
			const isPending: boolean = this.isPending;
			this.saving = true;
			let outcome: CompanyUserRoleOutcome;
			try {
				const manageCompanyAdminFunction = functions.httpsCallable('manageCompanyAdmin');
				// Pending invites have no userId, so canceling them is keyed by email instead.
				const result = await manageCompanyAdminFunction(buildRemovePayload({
					target: { email: this.targetEmail, userId: this.target?.userId || null },
					companyId: this.companyId,
				}));
				// The onCall wrapper answers with no data at all when the use case throws.
				outcome = removeOutcome(result?.data, { email: this.targetEmail, roleName, isPending });
			} catch (error) {
				console.error('Error removing company admin role:', error);
				outcome = { succeeded: false, message: removeFailureMessage(roleName) };
			}

			this.updateSnackbar({
				status: outcome.succeeded ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
				message: outcome.message,
				show: true,
			});
			if (outcome.succeeded) this.$emit('success');

			this.saving = false;
			if (resetDialog) resetDialog();
			this.onResetAndClose();
		},
	},
	components: {
		DoubleConfirmDangerDialog,
	},
});
